import React, { useEffect, useState } from 'react';
import { Switch, Route, Redirect, withRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { Auth } from 'aws-amplify';
import { ToastsContainer, ToastsStore } from 'react-toasts';
import {
  RelacaoMaquinas,
  RelacaoEstacoes,
  RelacaoClientes,
  RelacaoUsuarios,
  RelacaoVariaveis,
  CadastroVariavel,
  Dashboard,
  Login,
  Cliente,
  Aplicacao,
  Notificacoes,
  Admin,
  Relatorios,
  TodasVariaveis,
  Operacional,
  Eventos,
  CadastroSenha,
  RecuperarSenhaEmail,
  RecuperarSenhaResposta,
  RecuperarSenhaFinal,
  CadastrarNovaSenha,
  Register,
  Changelog,
  BackendChangelog,
  AuditLogs,
} from './screens';
import {
  Loader,
  GlobalStyle,
  SideMenu,
  VersionUpdateBanner,
  BottomNav,
  CommandPalette,
  SimulatorToggle,
} from './components';
import { SimulatorProvider } from './contexts/simulator';
import { isDeveloper } from './utils/simulator';
import { updateUserPreferences } from './services';
import light from './themes/light';
import dark from './themes/dark';

// Chave de cache local do tema. Guarda so o ROTULO ('light'/'dark'),
// nao o objeto de tema inteiro — assim, se a paleta mudar no codigo, a
// preferencia salva nao cristaliza cores antigas. Serve pra instant
// paint antes de /user/me responder; a fonte de verdade e o backend
// (state.usuario.preferences.theme), reconciliado quando o user carrega.
const THEME_CACHE_KEY = 'cco:theme';
const themeFromLabel = (label) => (label === 'dark' ? dark : light);
const readCachedThemeLabel = () => {
  try {
    return localStorage.getItem(THEME_CACHE_KEY) || 'light';
  } catch {
    return 'light';
  }
};

const App = () => {
  const dispatch = useDispatch();
  const { user, initialized } = useSelector((state) => state.usuario);
  const { quantidade } = useSelector((state) => state.eventos);
  // Preferencia de tema do perfil (backend). undefined ate /user/me
  // responder; quando chega, reconcilia com o paint local.
  const prefTheme = useSelector(
    (state) => state.usuario.preferences && state.usuario.preferences.theme
  );

  // Instant paint a partir do cache local (rotulo). Reconciliado com o
  // backend logo abaixo quando o user carrega.
  const [themeLabel, setThemeLabel] = useState(readCachedThemeLabel);
  const theme = themeFromLabel(themeLabel);

  // Reconcilia o tema com a preferencia do usuario quando ela chega do
  // backend. Server e fonte de verdade: se o operador setou 'dark' em
  // outro dispositivo, este aqui adota ao logar. Atualiza tambem o
  // cache local pro proximo instant paint.
  useEffect(() => {
    if (
      (prefTheme === 'light' || prefTheme === 'dark') &&
      prefTheme !== themeLabel
    ) {
      setThemeLabel(prefTheme);
      try {
        localStorage.setItem(THEME_CACHE_KEY, prefTheme);
      } catch {
        // localStorage indisponivel (modo privado / quota) — ignora,
        // o estado em memoria ja reflete a escolha nesta sessao.
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefTheme]);

  const handleChange = () => {
    const next = themeLabel === 'light' ? 'dark' : 'light';
    // 1. Aplica imediato (optimistic) + cache local pro instant paint.
    setThemeLabel(next);
    try {
      localStorage.setItem(THEME_CACHE_KEY, next);
    } catch {
      // ignore — ver comentario no effect de reconciliacao
    }
    // 2. Reflete no Redux pra qualquer consumidor de preferences.
    dispatch({ type: 'USUARIO/SET_PREFERENCES', preferences: { theme: next } });
    // 3. Persiste no perfil (fire-and-forget). So quando ha usuario
    // logado — na tela de login nao ha a quem associar. Falha silenciosa:
    // o cache local ja mantem a escolha; o proximo toggle tenta de novo.
    if (user) {
      updateUserPreferences({ theme: next }).catch(() => {});
    }
  };

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((currentUser) => {
        dispatch({
          type: 'USER/SAGA_SET_USER',
          user: currentUser,
        });
      })
      .catch(() => {
        dispatch({
          type: 'USER/SET_INITIALIZE',
          user: null,
        });
      });
  }, [dispatch]);

  useEffect(() => {
    dispatch({
      type: 'APP/SET_INITIALIZED',
      initialized: true,
    });
  }, [dispatch]);

  if (!initialized) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Loader />
      </div>
    );
  }

  const handleDashboardUserCustomerRedirect = () => {
    if (user.customer) {
      const isRecoveryRoute = [
        '/signup',
        '/forgot-password',
        '/forgot-password/verify',
        '/forgot-password/complete',
        '/reset-password',
      ].includes(window.location.pathname);

      return isRecoveryRoute ? null : <Redirect to="/dashboard/customer" />;
    }
    const isRecoveryRoute = [
      '/signup',
      '/forgot-password',
      '/forgot-password/verify',
      '/forgot-password/complete',
      '/reset-password',
    ].includes(window.location.pathname);

    return isRecoveryRoute ? null : <Redirect to="/dashboard" />;
  };

  const role = (user?.role || '').toString().trim().toLowerCase();

  return (
    <ThemeProvider theme={theme}>
      <SimulatorProvider>
        <ToastsContainer store={ToastsStore} />
        {/* Botao do MODO SIMULADOR — dados fake temporarios pra formatar
          layout. Renderizado SO pro desenvolvedor (isDeveloper); operador
          e cliente nunca veem. Flutuante (position fixed). */}
        {user && isDeveloper(user) && <SimulatorToggle />}
        {/* Banner de nova versao detectada via polling em /version.json.
          Aparece no topo-centro de qualquer rota e deixa o operador
          recarregar quando achar conveniente. */}
        <VersionUpdateBanner />
        {/* Ctrl+K: ver <CommandPalette/> abaixo (busca global de qualquer
          rota, com Recentes + Acoes). Substituiu o QuickSearch antigo, que
          dependia de abrir o /dashboard primeiro e duplicava o atalho. */}

        <div className="app-content" style={{ display: 'flex', flex: 1 }}>
          <GlobalStyle />
          <Switch>
            <Route path="/signup" exact component={CadastroSenha} />
            <Route
              path="/forgot-password"
              exact
              component={RecuperarSenhaEmail}
            />
            <Route
              path="/forgot-password/verify"
              exact
              component={RecuperarSenhaResposta}
            />
            <Route
              path="/forgot-password/complete"
              exact
              component={RecuperarSenhaFinal}
            />
            <Route
              path="/reset-password"
              exact
              component={CadastrarNovaSenha}
            />
            <Route path="/register/password" exact component={Register} />
          </Switch>
          {!user ? (
            <Switch>
              <Route path="/login" exact component={Login} />
              {window.location.pathname === '/signup' ||
              window.location.pathname === '/forgot-password' ||
              window.location.pathname === '/forgot-password/verify' ||
              window.location.pathname === '/forgot-password/complete' ||
              window.location.pathname === '/reset-password' ? null : (
                <Redirect to="/login" />
              )}
            </Switch>
          ) : (
            <>
              {window.location.pathname === '/signup' ||
              window.location.pathname === '/forgot-password' ||
              window.location.pathname === '/forgot-password/verify' ||
              window.location.pathname === '/forgot-password/complete' ||
              window.location.pathname === '/reset-password' ? null : (
                <>
                  <SideMenu handleChange={handleChange} />
                  {/* BottomNav so aparece em <=720px (CSS) — substitui o
                    rail no mobile. Renderizado junto pra compartilhar a
                    mesma condicao de rota/login. */}
                  <BottomNav handleChange={handleChange} />
                </>
              )}
              {!!quantidade.total && <Eventos />}
              {/* Busca global Ctrl+K — disponivel em qualquer tela logada. */}
              <CommandPalette />
              <Switch>
                {!user.customer && (
                  <Route path="/dashboard" exact component={Dashboard} />
                )}
                <Route path="/dashboard/customer" exact component={Cliente} />
                <Route
                  path="/dashboard/customer/plant"
                  exact
                  component={Aplicacao}
                />

                {role === 'higra' && (
                  <Route path="/customers" component={RelacaoClientes} />
                )}

                {role === 'higra' && (
                  <Route path="/stations" component={RelacaoEstacoes} />
                )}

                {role === 'higra' && (
                  <Route path="/machines" component={RelacaoMaquinas} />
                )}

                {(role === 'higra' || role === 'admin') && (
                  <Route path="/users" component={RelacaoUsuarios} />
                )}
                {role === 'higra' && (
                  <Route path="/variables" component={RelacaoVariaveis} />
                )}
                {role === 'higra' && (
                  <Route path="/variables/new" component={CadastroVariavel} />
                )}
                {role === 'higra' && (
                  <Route path="/audit-logs" component={AuditLogs} />
                )}
                <Route
                  path="/dashboard/customer/plant/appliance"
                  component={TodasVariaveis}
                />
                <Route
                  path="/dashboard/customer/operational"
                  component={Operacional}
                />
                <Route path="/reports" component={Relatorios} />
                <Route path="/changelog" exact component={Changelog} />
                <Route path="/changelog/backend" component={BackendChangelog} />
                <Route path="/notifications" component={Notificacoes} />
                <Route path="/admin" component={Admin} />
                {handleDashboardUserCustomerRedirect()}
              </Switch>
            </>
          )}
        </div>
      </SimulatorProvider>
    </ThemeProvider>
  );
};
export default withRouter(App);
